import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { motion, useAnimate, useReducedMotion } from 'motion/react'
import { ArrowLeft, Circle } from 'lucide-react'
import { Button } from '@/components/play'
import type { CameraFreshness } from './CameraSphere'
import { growthFrom, type GrowthBox } from './growth'
import './camera-sphere.css'

const SPRING = { type: 'spring' as const, stiffness: 380, damping: 36, mass: 0.8 }
const INSET = { width: 160, height: 100, zoom: 14 }

/** Web Mercator pixels at `zoom` for Mapbox's 512px tiles. */
function mercator(longitude: number, latitude: number, zoom: number) {
  const world = 512 * 2 ** zoom
  const lat = (latitude * Math.PI) / 180
  return { x: ((longitude + 180) / 360) * world, y: ((1 - Math.log(Math.tan(Math.PI / 4 + lat / 2)) / Math.PI) / 2) * world }
}

/**
 * A static map of the fix. The image is centred on a rounded position so it is
 * refetched only every ~100m; the pin is placed at the exact fix on top.
 */
export function insetMap(longitude: number, latitude: number, token: string | undefined) {
  if (!token) return null
  const center = { longitude: Math.round(longitude * 1000) / 1000, latitude: Math.round(latitude * 1000) / 1000 }
  const fix = mercator(longitude, latitude, INSET.zoom)
  const origin = mercator(center.longitude, center.latitude, INSET.zoom)
  return {
    src: `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/${center.longitude},${center.latitude},${INSET.zoom}/${INSET.width}x${INSET.height}@2x?attribution=false&logo=false&access_token=${token}`,
    // Percentages, so the pin stays true when the inset is drawn smaller on phones.
    pin: { left: `${((INSET.width / 2 + fix.x - origin.x) / INSET.width) * 100}%`, top: `${((INSET.height / 2 + fix.y - origin.y) / INSET.height) * 100}%` },
  }
}

export type CameraFullViewProps = {
  name: string
  /** A second element on the camera's track, so the preview underneath keeps playing. */
  video: ReactNode
  live?: boolean
  status?: string
  freshness?: CameraFreshness
  /** Shows a static inset of the fix; needs a Mapbox token. */
  location?: { latitude: number; longitude: number }
  token?: string
  /** The preview this view grows from and returns to. */
  source: () => Element | null
  /**
   * Where it grows from as it opens, read before anything moved (the preview's box as it was opened), for a surface
   * that makes room for the view as it opens (the watch screen's phone puts its canvas aside, which moves the map).
   * Left out, it grows from `source()` as it is then. It always returns to `source()`.
   */
  from?: GrowthBox | null
  onDismiss: () => void
  /** Surface actions beside the title, e.g. the workspace's camera controls. */
  actions?: ReactNode
  /**
   * The view is on screen (true when left out). A surface that keeps it mounted out of sight (the watch screen's Map
   * tab behind another tab) turns it off, so Escape meant for whatever is on screen doesn't close it.
   */
  enabled?: boolean
  className?: string
}

/**
 * The camera filling its map. It grows out of the camera's preview and
 * shrinks back into it, from a box of its own shape centred on the preview, so
 * the picture never squashes; Escape (while enabled; it marks the key used),
 * Back to map or the inset returns.
 */
export function CameraFullView({ name, video, live = false, status, freshness, location, token, source, from: opensFrom, onDismiss, actions, enabled = true, className }: CameraFullViewProps) {
  const reducedMotion = useReducedMotion()
  const [scope, animate] = useAnimate<HTMLDivElement>()
  const close = useRef<HTMLButtonElement>(null)
  const closing = useRef(false)
  const frame = () => scope.current?.querySelector<HTMLElement>('.cs-full__frame') ?? null

  function offsetFrom(start?: GrowthBox | null) {
    const target = frame()
    const from = start ?? source()?.getBoundingClientRect()
    const to = target?.getBoundingClientRect()
    if (reducedMotion || !target || !from || !to || !to.width || !to.height || !from.width || !from.height) return null
    const { x, y, scale } = growthFrom(from, to)
    return { x, y, scaleX: scale, scaleY: scale }
  }

  useLayoutEffect(() => {
    const offset = offsetFrom(opensFrom)
    if (offset) {
      animate('.cs-full__frame', { x: [offset.x, 0], y: [offset.y, 0], scaleX: [offset.scaleX, 1], scaleY: [offset.scaleY, 1] }, SPRING)
    }
    close.current?.focus({ preventScroll: true })
  }, [])

  async function dismiss() {
    if (closing.current) return
    closing.current = true
    const offset = offsetFrom()
    if (offset) {
      animate('.cs-full__chrome', { opacity: 0 }, { duration: 0.1 })
      await animate('.cs-full__frame', { x: offset.x, y: offset.y, scaleX: offset.scaleX, scaleY: offset.scaleY }, { ...SPRING, stiffness: 520 })
    }
    onDismiss()
  }

  const dismissRef = useRef(dismiss)
  useLayoutEffect(() => {
    dismissRef.current = dismiss
  })
  useEffect(() => {
    if (!enabled) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      event.preventDefault()
      void dismissRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [enabled])

  const inset = location ? insetMap(location.longitude, location.latitude, token) : null
  return (
    <div ref={scope} className={`cs-full ${actions ? 'cs-full--with-actions' : ''} ${className ?? ''}`} role="region" aria-label={`${name}, full view`}>
      <div className="cs-full__frame">{video}</div>
      <motion.div className="cs-full__chrome" initial={reducedMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: reducedMotion ? 0 : 0.16, duration: 0.18 }}>
        <div className="cs-full__title">
          {live ? (
            <span className="cs-full__state cs-full__state--live">
              <Circle size={10} fill="currentColor" aria-hidden />
              Live
            </span>
          ) : (
            status && <span className="cs-full__state">{status}</span>
          )}
          <strong>{name}</strong>
          {freshness && <span className={`cs-full__age ${freshness.stale ? 'cs-full__age--stale' : ''}`}>{freshness.label}</span>}
        </div>
        <div className="cs-full__actions">
          {actions}
          <Button ref={close} variant="secondary" elevated onClick={() => void dismiss()}>
            <ArrowLeft aria-hidden />
            Back to map
          </Button>
        </div>
        {inset && (
          <button type="button" className="cs-full__inset" aria-label={`Show ${name} on the map`} title="Show on the map" onClick={() => void dismiss()}>
            <img src={inset.src} alt="" width={INSET.width} height={INSET.height} />
            <span className="cs-full__pin" style={{ left: inset.pin.left, top: inset.pin.top }} aria-hidden />
          </button>
        )}
      </motion.div>
    </div>
  )
}
